import { EventApi, EventMountArg } from "@fullcalendar/core";
import { useRef } from "react";
import { isModalOrDialogOpen } from "../../hotkeys/isTyping";
import { CalendarEvent, ClientTimeSlot, isGroupTherapistType } from "./controller/adapters";
import { eventToClientTimeSlot } from "./controller/adapters/display-to-client/reverse.adapter";

type EventKey = string;
type CellKey = string;
type CellPos = {
  resourceIdx: number;
  rowIdx: number;
};
const minutesSinceMidnight = (d: Date): number => d.getHours() * 60 + d.getMinutes();

const toEventKey = (resourceId: string, startMin: number, endMin: number): EventKey =>
  `${resourceId}:${startMin}:${endMin}`;

// *************************** Overlap detection ***************************
// Resolve the rendered event harness that overlaps a given slot directly from the
// live DOM. Doing this at focus time (instead of consulting a cached overlap map)
// guarantees the overlap decision always matches what is currently on screen, so a
// stale/rebuilt event set can never trap focus on the empty 5-minute cell.
const findLiveOverlapHarness = (
  root: HTMLElement,
  resourceId: string,
  slotMin: number
): HTMLElement | null => {
  const harnesses = root.querySelectorAll<HTMLElement>(".fc-timegrid-event-harness");
  let best: HTMLElement | null = null;
  let bestStart = -Infinity;

  harnesses.forEach((harness) => {
    if (harness.dataset.resourceId !== resourceId) return;
    const startMin = Number(harness.dataset.startMin);
    const endMin = Number(harness.dataset.endMin);
    if (Number.isNaN(startMin) || Number.isNaN(endMin)) return;
    // Prefer the rightmost (latest-starting) event that contains the slot, matching
    // how overlapping events were previously disambiguated.
    if (startMin <= slotMin && slotMin < endMin && startMin > bestStart) {
      best = harness;
      bestStart = startMin;
    }
  });

  return best;
};

// *********************** helpers for event set handler *******************

const parseTimeStringToMinutes = (time?: string | null) => {
  if (!time) return null;
  const [hours = "0", minutes = "0"] = time.split(":");
  const hoursInt = parseInt(hours, 10);
  const minutesInt = parseInt(minutes, 10);
  if (Number.isNaN(hoursInt) || Number.isNaN(minutesInt)) return null;
  return hoursInt * 60 + minutesInt;
};

export const toCellKey = (resourceId: string, slotMin: number): CellKey =>
  `${resourceId}|${slotMin}`;

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

export const timeToSlotMin = (timeStr: string): number | null => {
  const [hh = "0", mm = "0"] = timeStr.split(":");
  const h = Number(hh);
  const m = Number(mm);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/*************************** helpers for keyboard handling ********************************************** */
type FocusState =
  | { kind: "cell"; resourceId: string; slotMin: number }
  | { kind: "event"; resourceId: string; startMin: number; endMin: number }
  | null;

const getFocusState = (root: HTMLElement): FocusState => {
  const active = document.activeElement as HTMLElement | null;
  if (!active) return null;
  if (!root.contains(active)) return null;

  const eventHarness = active.closest(".fc-timegrid-event-harness") as HTMLElement | null;
  if (eventHarness) {
    const resourceId = eventHarness.dataset.resourceId;
    const startMin = Number(eventHarness.dataset.startMin);
    const endMin = Number(eventHarness.dataset.endMin);
    if (!resourceId || Number.isNaN(startMin) || Number.isNaN(endMin)) return null;
    return { kind: "event", resourceId, startMin, endMin };
  }

  const slot = active.closest(
    ".fc-timegrid-slot-resource, .fc-timegrid-slot"
  ) as HTMLElement | null;
  if (slot) {
    const resourceId =
      slot.dataset.resourceId ??
      (slot.closest("td[data-resource-id]") as HTMLElement | null)?.dataset.resourceId;
    const slotMin = Number(slot.dataset.slotMin);
    if (!resourceId || Number.isNaN(slotMin)) return null;
    return { kind: "cell", resourceId, slotMin };
  }

  return null;
};

const slotDurationMin = 5;

const slotMinToRowIdx = (slotMins: number[], slotMin: number): number => {
  // first index with value >= slotMin
  let lo = 0;
  let hi = slotMins.length - 1;
  let ans = slotMins.length;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (slotMins[mid] >= slotMin) {
      ans = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return clamp(ans, 0, slotMins.length - 1);
};

type FocusCell = { resourceId: string; slotMin: number } | null;

const slotMinToTimeStr = (slotMin: number): string => {
  const hh = String(Math.floor(slotMin / 60)).padStart(2, "0");
  const mm = String(slotMin % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};


// *************************** Hook ***************************
export type CellFocusInfo = {
  cell: HTMLElement;
  cellTime: string;           // The logical row time (from the cell)
  eventStartTime?: string;    // The event's start time (if an event is focused)
  isOnEvent: boolean;
};

type UseCellNavigationArgs = {
  getRootEl: () => HTMLElement | null; // e.g. a ref.current
  hasPatientResource: boolean;
  onSelectedAppointmentChange?: (slot: ClientTimeSlot | null) => void;
  onCellFocusChange?: (info: CellFocusInfo) => void;
  /**
   * Called when the cursor is asked to move past the first/last resource column.
   * The single-therapist view uses it to widen its date window, so arrowing off
   * the edge pulls in more days. Without it the cursor would just clamp, and on a
   * screen wide enough to show every column at once (no horizontal overflow, so
   * no scroll events) the window could never grow at all.
   */
  onReachEdge?: (edge: "start" | "end") => void;
};
export const useCellNavigation = ({
  getRootEl,
  hasPatientResource,
  onSelectedAppointmentChange,
  onCellFocusChange,
  onReachEdge,
}: UseCellNavigationArgs) => {
  const lastFocusedEventRef = useRef<HTMLElement | null>(null);
  const selectedAppointmentRef = useRef<ClientTimeSlot | null>(null);
  const cellKeyToElRef = useRef<Map<CellKey, HTMLElement>>(new Map());
  const resourceIdsRef = useRef<string[]>([]);
  const slotMinsRef = useRef<number[]>([]);
  const currentPosRef = useRef<CellPos | null>(null);

  const eventKeyToElementRef = useRef<Map<string, HTMLElement>>(new Map());
  // element -> ClientTimeSlot, maintained on mount/unmount so it stays in sync with
  // the rendered harnesses (used by handleFocus to open the matching appointment).
  const harnessClientSlotRef = useRef<WeakMap<HTMLElement, ClientTimeSlot>>(new WeakMap());

  const lastEmittedAppointmentIdRef = useRef<string | null>(null);

  const emitSelectedAppointment = (slot: ClientTimeSlot | null) => {
    const id = slot?.id ?? null; // or appointmentId, whatever is stable in ClientTimeSlot
    if (id === lastEmittedAppointmentIdRef.current) return;
    lastEmittedAppointmentIdRef.current = id;
    onSelectedAppointmentChange?.(slot);
  };

  const focusedCellRef = useRef<FocusCell>(null);

  const setFocusedCell = (resourceId: string, slotMin: number): void => {
    focusedCellRef.current = { resourceId, slotMin };

    // also keep your grid cursor in sync (optional but nice)
    const rIdx = resourceIdsRef.current.indexOf(resourceId);
    const rowIdx = slotMinToRowIdx(slotMinsRef.current, slotMin);
    if (rIdx >= 0 && rowIdx >= 0) currentPosRef.current = { resourceIdx: rIdx, rowIdx };
  };

  const getFocusedCell = (): (FocusCell & { timeStr: string }) | null => {
    const c = focusedCellRef.current;
    if (!c) return null;
    return { ...c, timeStr: slotMinToTimeStr(c.slotMin) };
  };

  const eventDidMount_attachElement = (arg: EventMountArg) => {
    const resourceId = arg.event.getResources()?.[0]?.id;
    if (!resourceId || !arg.event.start || !arg.event.end) return;

    const startMin = minutesSinceMidnight(arg.event.start);
    const endMin = minutesSinceMidnight(arg.event.end);

    const key = toEventKey(resourceId, startMin, endMin);

    // Get the event harness element (parent of .fc-event) for focusing
    const harness = arg.el.closest(".fc-timegrid-event-harness") as HTMLElement | null;
    const focusEl = harness ?? arg.el;

    // Store event metadata on element for navigation lookups
    focusEl.dataset.resourceId = resourceId;
    focusEl.dataset.startMin = String(startMin);
    focusEl.dataset.endMin = String(endMin);
    focusEl.dataset.eventKey = key;

    // Add marker class for GroupTimeSlotTherapist events
    const extendedProps = arg.event.extendedProps;
    if (extendedProps && isGroupTherapistType(extendedProps.timeSlotType)) {
      focusEl.classList.add("group-therapist");
    }

    // Cache the ClientTimeSlot alongside the element so focus selection always has
    // the matching appointment payload without a separate (drift-prone) lookup map.
    let clientSlot: ClientTimeSlot | undefined;
    try {
      clientSlot = eventToClientTimeSlot(arg.event as unknown as CalendarEvent);
    } catch {
      clientSlot = undefined;
    }
    if (clientSlot) harnessClientSlotRef.current.set(focusEl, clientSlot);
    else harnessClientSlotRef.current.delete(focusEl);

    eventKeyToElementRef.current.set(key, focusEl);
    focusEl.setAttribute("tabindex", "-1");
  };

  const eventWillUnmount_detachElement = (arg: EventMountArg) => {
    const el = (arg.el.closest(".fc-timegrid-event-harness") as HTMLElement | null) ?? arg.el;
    const key = el.dataset.eventKey;
    if (key) eventKeyToElementRef.current.delete(key);
  };

  const clearFocusedEvent = () => {
    const prev = lastFocusedEventRef.current;
    if (prev) prev.classList.remove("focused");
    lastFocusedEventRef.current = null;
  };

  const handleFocus = (slot: HTMLElement) => {
    document
      .querySelectorAll(".fc-event.selected")
      .forEach((el) => el.classList.remove("selected"));

    const slotTime = slot.getAttribute("data-time");
    const resourceId =
      slot.getAttribute("data-resource-id") ??
      slot.closest("td[data-resource-id]")?.getAttribute("data-resource-id") ??
      null;

    const slotMin = parseTimeStringToMinutes(slotTime);
    if (!resourceId || slotMin == null || !slotTime) {
      selectedAppointmentRef.current = null;
      clearFocusedEvent();
      return;
    }

    setFocusedCell(resourceId, slotMin);

    // Keep the logical navigation position in sync with the actually focused cell
    // (mouse or keyboard). Without this, currentPosRef only tracks keyboard moves,
    // so after a DOM rebuild (e.g. infinite scroll) arrow keys would resume from a
    // stale position and jump to an unrelated day.
    const focusedResourceIdx = resourceIdsRef.current.indexOf(resourceId);
    if (focusedResourceIdx >= 0) {
      currentPosRef.current = {
        resourceIdx: focusedResourceIdx,
        rowIdx: slotMinToRowIdx(slotMinsRef.current, slotMin),
      };
    }

    // Resolve the overlapping event from the LIVE DOM rather than a cached overlap
    // map. The previous cached map (rebuilt on eventsSet) could drift out of sync
    // with the element map (maintained on mount/unmount); when it did, focus stayed
    // trapped on the 5-minute cell instead of redirecting to the event. Reading the
    // rendered harnesses keeps the overlap decision and the target element in sync.
    const root = getRootEl();
    const eventEl = root ? findLiveOverlapHarness(root, resourceId, slotMin) : null;

    // Notify callback with cell info and optional event start time
    if (onCellFocusChange) {
      let eventStartTime: string | undefined;
      if (eventEl) {
        const eventStartMin = Number(eventEl.dataset.startMin);
        if (!Number.isNaN(eventStartMin)) {
          eventStartTime = `${slotMinToTimeStr(eventStartMin)}:00`; // consumers expect HH:MM:SS
        }
      }

      onCellFocusChange({
        cell: slot,
        cellTime: slotTime,
        eventStartTime,
        isOnEvent: !!eventEl,
      });
    }

    // No overlapping event - stay on the cell
    if (!eventEl) {
      selectedAppointmentRef.current = null;
      clearFocusedEvent();
      emitSelectedAppointment(null);
      return;
    }

    // GroupTimeSlotTherapist events get visual focus but never open the drawer
    const isGroupTherapist = eventEl.classList.contains("group-therapist");
    if (isGroupTherapist) {
      selectedAppointmentRef.current = null;
      emitSelectedAppointment(null);
    } else {
      const clientSlot = harnessClientSlotRef.current.get(eventEl) ?? null;
      selectedAppointmentRef.current = clientSlot;
      emitSelectedAppointment(clientSlot);
    }

    if (lastFocusedEventRef.current !== eventEl) {
      clearFocusedEvent();
      lastFocusedEventRef.current = eventEl;
      eventEl.classList.add("focused");
    }

    eventEl.focus();
  };

  const eventSet_attachFocusHandlers = (events: EventApi[]) => {
    // Overlap is now resolved from the live DOM in handleFocus, so there is no
    // cached overlap map to rebuild here. Kept for the eventsSet wiring.
    void events;
  };

  const attachDelegatedEventListeners = () => {
    const root = getRootEl();
    if (!root) return () => undefined;

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // match either a real fc slot or your custom slots
      const slot =
        target.closest(".fc-timegrid-slot-resource") ?? target.closest(".fc-timegrid-slot") ?? null;

      if (!slot) return;

      handleFocus(slot as HTMLElement);
    };

    root.addEventListener("focusin", onFocusIn, true);

    return () => {
      root.removeEventListener("focusin", onFocusIn, true);
    };
  };

  /**
   * Handles clicking on empty cells - should be called from dateClick handler
   * @param resourceId 
   * @param dateObj 
   * @returns 
   */
  const dateClick_HandleEmptyCells = (resourceId: string | undefined, dateObj: Date | undefined) => {
    if (!resourceId || !dateObj) return false;

    const rawMin = minutesSinceMidnight(dateObj);
    const snappedMin = Math.floor(rawMin / 5) * 5;
    const cellKey = toCellKey(resourceId, snappedMin);

    const cellEl = cellKeyToElRef.current.get(cellKey);
    if (!cellEl) {
      return false;
    }

    // Focus triggers focusin → handleFocus which calls onCellFocusChange
    cellEl.focus();
    
    return true;
  };

  const isTypingContext = (activeEl: HTMLElement | null): boolean => {
    if (!activeEl) return false;
    const tag = activeEl.tagName.toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select" || activeEl.isContentEditable)
      return true;

    // MUI-ish
    if (
      activeEl.closest('[role="combobox"]') ||
      activeEl.closest('[role="textbox"]') ||
      activeEl.closest(".MuiAutocomplete-input") ||
      activeEl.closest(".MuiInput-input") ||
      activeEl.closest(".MuiOutlinedInput-input") ||
      activeEl.closest(".MuiFilledInput-input")
    )
      return true;

    return false;
  };

  const ensureInitialized = (): CellPos | null => {
    const resources = resourceIdsRef.current;
    const rows = slotMinsRef.current;
    if (!resources.length || !rows.length) return null;

    if (currentPosRef.current) return currentPosRef.current;

    // pick first cell
    const pos: CellPos = { resourceIdx: 0, rowIdx: 0 };
    currentPosRef.current = pos;
    return pos;
  };

  const focusCellAt = (pos: CellPos): boolean => {
    const resources = resourceIdsRef.current;
    const rows = slotMinsRef.current;
    const resourceId = resources[pos.resourceIdx];
    const slotMin = rows[pos.rowIdx];
    if (!resourceId || slotMin == null) return false;

    const el = cellKeyToElRef.current.get(toCellKey(resourceId, slotMin));
    if (!el) return false;

    // keep state in sync before focusing
    currentPosRef.current = pos;

    // Focus triggers focusin → handleFocus which calls onCellFocusChange
    el.focus();

    return true;
  };

  /**
   * Re-focus the cell the cursor is logically on. Widening the date window
   * rebuilds every cell `<td>`, which drops DOM focus to `<body>` and leaves the
   * user pressing arrow keys with nothing focused. Callers invoke this after a
   * rebuild so keyboard navigation continues from where it left off.
   */
  const refocusCurrentCell = (): boolean => {
    const pos = currentPosRef.current;
    return pos ? focusCellAt(pos) : false;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isTypingContext(document.activeElement as HTMLElement | null)) return;
    // Dialogs own the keyboard while open: Enter belongs to the dialog's
    // default action, arrows to its controls — never to the calendar cursor.
    if (isModalOrDialogOpen()) return;

    const root = getRootEl();
    if (!root) return;

    // Cell cursor moves are driven by the arrow keys AND the i/j/k/l "triangle"
    // (i = up, k = down, j = left, l = right) as bare keys. Shift+i / Shift+k
    // jump 10 rows (a "page" of 5-min cells) up / down. Shifted J/L are the
    // calendar's page-left / page-right DATE hotkeys (handled by the central
    // provider), so they must NOT be treated as cell moves here.
    // Contract: hotkeys/keyboardSpec.ts → cellMove / cellJump.
    const lower = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    let direction: "up" | "down" | "left" | "right" | null = null;
    let step = 1;
    if (e.key === "ArrowUp") direction = "up";
    else if (e.key === "ArrowDown") direction = "down";
    else if (e.key === "ArrowLeft") direction = "left";
    else if (e.key === "ArrowRight") direction = "right";
    else if (lower === "i") {
      direction = "up";
      step = e.shiftKey ? 10 : 1;
    } else if (lower === "k") {
      direction = "down";
      step = e.shiftKey ? 10 : 1;
    } else if (lower === "j" && !e.shiftKey) direction = "left";
    else if (lower === "l" && !e.shiftKey) direction = "right";
    const isAction = e.key === "Enter" || e.key === "f";
    if (!direction && !isAction) return;

    const resources = resourceIdsRef.current;
    const rows = slotMinsRef.current;
    if (!resources.length || !rows.length) return;

    const pos0 = ensureInitialized();
    if (!pos0) return;

    const focusState = getFocusState(root);

    e.preventDefault();

    // Determine base position for navigation
    // Key insight: currentPosRef tracks the logical row we're on, even when focused on an event.
    // This is important for left/right navigation - we want to exit an event at the same row we entered.
    let basePos: CellPos = { ...pos0 };
    
    if (focusState?.kind === "cell") {
      // When on a cell, sync position from the actual focused cell
      const rIdx = resources.indexOf(focusState.resourceId);
      if (rIdx >= 0)
        basePos = { resourceIdx: rIdx, rowIdx: slotMinToRowIdx(rows, focusState.slotMin) };
    } else if (focusState?.kind === "event") {
      // When on an event, behavior depends on navigation direction:
      // - For left/right: preserve the rowIdx from currentPosRef (the row we entered from)
      // - For up/down: we'll handle jumping over the event below
      const rIdx = resources.indexOf(focusState.resourceId);
      if (rIdx >= 0) {
        // Use preserved rowIdx from currentPosRef for horizontal navigation
        // This ensures we exit the event at the same row we entered
        const preservedRowIdx = currentPosRef.current?.rowIdx ?? slotMinToRowIdx(rows, focusState.startMin);
        basePos = { resourceIdx: rIdx, rowIdx: preservedRowIdx };
      }
    }

    let next: CellPos = { ...basePos };

    if (direction === "down") {
      // Single-step over an event jumps to the first slot AFTER it; a multi-row
      // "page" jump instead advances `step` rows from the entry row.
      if (focusState?.kind === "event" && step === 1) {
        next.rowIdx = slotMinToRowIdx(rows, focusState.endMin);
      } else {
        next.rowIdx = clamp(basePos.rowIdx + step, 0, rows.length - 1);
      }
    }

    if (direction === "up") {
      // Single-step over an event jumps to the last slot BEFORE it; a multi-row
      // "page" jump instead retreats `step` rows from the entry row.
      if (focusState?.kind === "event" && step === 1) {
        next.rowIdx = slotMinToRowIdx(rows, focusState.startMin - slotDurationMin);
      } else {
        next.rowIdx = clamp(basePos.rowIdx - step, 0, rows.length - 1);
      }
    }

    if (direction === "right") {
      // For horizontal navigation, rowIdx stays the same (preserves entry row)
      next.resourceIdx = clamp(basePos.resourceIdx + 1, 0, resources.length - 1);
      // Already on the last column: ask the view for more days instead of
      // silently clamping. The cursor stays put; it advances once the new
      // columns have rendered and the user presses again.
      if (next.resourceIdx === basePos.resourceIdx) {
        onReachEdge?.("end");
        return;
      }
    }

    if (direction === "left") {
      // For horizontal navigation, rowIdx stays the same (preserves entry row)
      next.resourceIdx = clamp(basePos.resourceIdx - 1, 0, resources.length - 1);
      if (next.resourceIdx === basePos.resourceIdx) {
        onReachEdge?.("start");
        return;
      }
    }

    // Update pos + focus the cell. If it overlaps an event, your focusin handler will redirect to event.
    const ok = focusCellAt(next);
    if (!ok) {
      currentPosRef.current = basePos;
      return;
    }
  };

  const setResources = (resources: { id: string }[]) => {
    // Remember which date column the logical focus position is currently anchored to,
    // so we can keep tracking the same day after the resource set changes.
    const prevResourceIds = resourceIdsRef.current;
    const prevPos = currentPosRef.current;
    const anchoredResourceId =
      prevPos && prevResourceIds[prevPos.resourceIdx] != null
        ? prevResourceIds[prevPos.resourceIdx]
        : null;

    // MD-515: keep the caller's order. This is the column order the board
    // renders, and left/right moves by index through it. Sorting by id — a
    // UUID — made `resourceIdx + 1` mean "the next column alphabetically by
    // user id", which is unrelated to what is on screen. Re-anchoring below
    // uses indexOf and never depended on the sort.
    const nextResourceIds = resources
      .map((r) => r.id)
      .filter((id) => id !== "0");

    resourceIdsRef.current = nextResourceIds;

    // Re-anchor the logical position to the same date column. Infinite scrolling can
    // prepend earlier days, which shifts every existing column's index; without this
    // remap, index-based navigation would jump to a different day.
    if (prevPos) {
      if (anchoredResourceId != null) {
        const newIdx = nextResourceIds.indexOf(anchoredResourceId);
        currentPosRef.current = {
          ...prevPos,
          resourceIdx:
            newIdx >= 0
              ? newIdx
              : clamp(prevPos.resourceIdx, 0, Math.max(0, nextResourceIds.length - 1)),
        };
      } else {
        currentPosRef.current = {
          ...prevPos,
          resourceIdx: clamp(prevPos.resourceIdx, 0, Math.max(0, nextResourceIds.length - 1)),
        };
      }
    }
  };

  const parseHHMMSS = (s: string): number => {
    const [hh, mm, ss] = s.split(":");
    const h = Number(hh ?? 0);
    const m = Number(mm ?? 0);
    const sec = Number(ss ?? 0);
    return h * 60 + m + (sec >= 30 ? 1 : 0); // safe rounding
  };

  const buildSlotMins = (slotMinTime: string, slotMaxTime: string, slotDurationMin: number) => {
    const start = parseHHMMSS(slotMinTime);
    const end = parseHHMMSS(slotMaxTime);
    const out: number[] = [];
    for (let t = start; t < end; t += slotDurationMin) out.push(t);
    slotMinsRef.current = out;
  };

  // Initialize slot times (7:00 AM to 9:00 PM in 5-minute intervals)
  buildSlotMins("07:00:00", "21:00:00", 5);

  const attachMouseHoverListeners = (onMouseHover?: (timeSlot: string | null) => void) => {
    const root = getRootEl();
    if (!root) return () => {};

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const slot = target.closest('.fc-timegrid-slot') as HTMLElement;
      if (slot) {
        const timeStr = slot.getAttribute('data-time');
        if (timeStr) {
          // If onMouseHover callback is provided, use it; otherwise fall back to onCellFocusChange
          if (onMouseHover) {
            onMouseHover(timeStr);
          } 
        }
      }
    };

    // Attach listener to root using event delegation (mouseover bubbles, mouseenter doesn't)
    root.addEventListener('mouseover', handleMouseOver);

    // Return cleanup function
    return () => {
      root.removeEventListener('mouseover', handleMouseOver);
    };
  };

  return {
    eventKeyToElementRef,
    eventDidMount_attachElement,
    eventWillUnmount_detachElement,
    eventSet_attachFocusHandlers,
    attachDelegatedEventListeners,
    cellKeyToElRef,
    dateClick_HandleEmptyCells,
    selectedAppointmentRef,
    handleKeyDown,
    refocusCurrentCell,
    setResources,
    resourceIdsRef,
    slotMinsRef,
    currentPosRef,
    getFocusedCell,
    attachMouseHoverListeners,
  };
};
